import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getGreeting, formatCurrency, getStatusClass, hasPermission } from '../lib/utils';
import { claimsAPI, approvalsAPI, paymentsAPI, misAPI, seedAPI } from '../lib/api';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  Plus,
  ArrowRight,
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  RefreshCw
} from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClaims: 0,
    pendingApprovals: 0,
    pendingPayments: 0,
    recentClaims: [],
    misSummary: null
  });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const position = user?.user_position;

      // Fetch claims for field staff
      if (hasPermission(position, ['RBO-Head', 'RBO-Deputy Head', 'Field Manager', 'Additional Field Manager', 'Wealth Advisor'])) {
        const claimsRes = await claimsAPI.getMyClaims();
        const claims = claimsRes.data;
        setStats(prev => ({
          ...prev,
          totalClaims: claims.length,
          recentClaims: claims.slice(0, 5)
        }));
      }

      // Fetch pending approvals for approvers
      if (hasPermission(position, ['RBO-Head', 'Federation-President', 'Fed-Site-Mgt-Head'])) {
        try {
          const approvalsRes = await approvalsAPI.getPending();
          setStats(prev => ({
            ...prev,
            pendingApprovals: approvalsRes.data.length
          }));
        } catch (e) {
          console.log('No approvals access');
        }
      }

      // Fetch pending payments for finance
      if (hasPermission(position, ['Fed-Finance-Head', 'Federation-President'])) {
        try {
          const paymentsRes = await paymentsAPI.getPending();
          setStats(prev => ({
            ...prev,
            pendingPayments: paymentsRes.data.length
          }));
        } catch (e) {
          console.log('No payments access');
        }
      }

      // Fetch MIS for management
      if (hasPermission(position, ['CVO', 'CEO', 'Federation-President', 'Fed-Site-Mgt-Head', 'Fed-Finance-Head'])) {
        try {
          const misRes = await misAPI.getSummary();
          setStats(prev => ({
            ...prev,
            misSummary: misRes.data
          }));
        } catch (e) {
          console.log('No MIS access');
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedAPI.seedOrganization();
      await seedAPI.seedDropdown();
      toast.success('Database seeded successfully! You can now register users.');
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
    <Card 
      className={`card-hover cursor-pointer ${onClick ? '' : 'cursor-default'}`}
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const position = user?.user_position;

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {getGreeting()}, {user?.user_name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.federation_name && `${user.federation_name}`}
              {user?.rbo_name && ` • ${user.rbo_name}`}
            </p>
          </div>

          <div className="flex gap-2">
            {/* Seed button for initial setup */}
            <Button
              variant="outline"
              onClick={handleSeedData}
              disabled={seeding}
              data-testid="seed-data-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${seeding ? 'animate-spin' : ''}`} />
              {seeding ? 'Seeding...' : 'Seed Data'}
            </Button>

            {hasPermission(position, ['RBO-Head', 'RBO-Deputy Head', 'Field Manager', 'Additional Field Manager', 'Wealth Advisor']) && (
              <Button onClick={() => navigate('/claims/new')} data-testid="new-claim-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Claim
              </Button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hasPermission(position, ['RBO-Head', 'RBO-Deputy Head', 'Field Manager', 'Additional Field Manager', 'Wealth Advisor']) && (
            <StatCard
              title="My Claims"
              value={stats.totalClaims}
              icon={FileText}
              color="bg-blue-100 text-blue-600"
              onClick={() => navigate('/claims')}
            />
          )}

          {hasPermission(position, ['RBO-Head', 'Federation-President', 'Fed-Site-Mgt-Head']) && (
            <StatCard
              title="Pending Approvals"
              value={stats.pendingApprovals}
              icon={Clock}
              color="bg-yellow-100 text-yellow-600"
              onClick={() => navigate('/approvals')}
            />
          )}

          {hasPermission(position, ['Fed-Finance-Head', 'Federation-President']) && (
            <StatCard
              title="Pending Payments"
              value={stats.pendingPayments}
              icon={CreditCard}
              color="bg-green-100 text-green-600"
              onClick={() => navigate('/payments')}
            />
          )}

          {stats.misSummary && (
            <StatCard
              title="Total Claims"
              value={stats.misSummary.total_claims}
              icon={BarChart3}
              color="bg-purple-100 text-purple-600"
              onClick={() => navigate('/mis')}
            />
          )}
        </div>

        {/* MIS Summary for Management */}
        {stats.misSummary && (
          <Card data-testid="mis-summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Claims Overview
              </CardTitle>
              <CardDescription>Summary of all claims across the organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(stats.misSummary.status_counts).map(([status, count]) => (
                  <div key={status} className="text-center p-4 bg-slate-50 rounded-lg">
                    <span className={`status-badge ${getStatusClass(status)}`}>{status}</span>
                    <p className="text-2xl font-bold mt-2">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Claims for Field Staff */}
        {stats.recentClaims.length > 0 && (
          <Card data-testid="recent-claims-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Claims</CardTitle>
                <CardDescription>Your latest advance claim submissions</CardDescription>
              </div>
              <Button variant="ghost" onClick={() => navigate('/claims')} data-testid="view-all-claims">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.recentClaims.map((claim) => (
                  <div
                    key={claim.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => navigate(`/claims/${claim.id}`)}
                    data-testid={`claim-row-${claim.claim_number}`}
                  >
                    <div>
                      <p className="font-medium">{claim.claim_number}</p>
                      <p className="text-sm text-muted-foreground">
                        {claim.project_site_location} • {claim.date_of_travel}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`status-badge ${getStatusClass(claim.status)}`}>
                        {claim.status}
                      </span>
                      <p className="text-sm font-medium mt-1">
                        {formatCurrency(claim.calculations?.advance_claim_amount)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {hasPermission(position, ['RBO-Head', 'RBO-Deputy Head', 'Field Manager', 'Additional Field Manager', 'Wealth Advisor']) && (
                <>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => navigate('/claims/new')}
                    data-testid="quick-new-claim"
                  >
                    <Plus className="w-5 h-5" />
                    <span>New Claim</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2"
                    onClick={() => navigate('/claims')}
                    data-testid="quick-my-claims"
                  >
                    <FileText className="w-5 h-5" />
                    <span>My Claims</span>
                  </Button>
                </>
              )}

              {hasPermission(position, ['RBO-Head', 'Federation-President', 'Fed-Site-Mgt-Head']) && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => navigate('/approvals')}
                  data-testid="quick-approvals"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Approvals</span>
                </Button>
              )}

              {hasPermission(position, ['Fed-Finance-Head', 'Federation-President']) && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => navigate('/payments')}
                  data-testid="quick-payments"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Payments</span>
                </Button>
              )}

              {hasPermission(position, ['Project Site Manager']) && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => navigate('/reports/daily')}
                  data-testid="quick-daily-report"
                >
                  <Building2 className="w-5 h-5" />
                  <span>Daily Report</span>
                </Button>
              )}

              {hasPermission(position, ['CVO', 'CEO', 'Federation-President', 'Fed-Site-Mgt-Head', 'Fed-Finance-Head']) && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                  onClick={() => navigate('/mis')}
                  data-testid="quick-mis"
                >
                  <BarChart3 className="w-5 h-5" />
                  <span>MIS Reports</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
